import {
  TERM_FORMS,
  TERM_KEYS,
  WORD_SETS,
  termName,
  termReadings,
  termVariables,
  type TermLang,
  type Vocabulary,
} from '@attendance/shared/terminology';
import i18n from './i18n.js';

/* ===========================================================================
 * HOW THE COMPANY'S WORDS REACH THE SCREEN — two doors, one source.
 *
 *   1. i18n DEFAULT VARIABLES, under `term.`. Every translated string can
 *      say `{{term.Employees}}`, `{{term.worksite}}`, `{{term.teamDef}}` (the
 *      full list: `termVariables` in packages/shared/src/terminology.ts) and
 *      get the company's word in the reader's language, without its screen
 *      passing anything. The namespace is what makes that safe: no screen
 *      passes a `term`, so a string's own `{{worksite}}` (a site's NAME, a
 *      count) can never be shadowed by — or shadow — the company's word. The
 *      nav rail is labelled this way — "{{term.Team}} directory" in English,
 *      "دليل {{term.teamDef}}" in Arabic — which is what retired the
 *      English concatenations ("فرقة directory") and the rule that skipped
 *      every built-in word, so the construction set never reached the rail.
 *
 *   2. `useTerminology()` (stores/brandingStore.ts), for code that needs a word
 *      as a value — a column caption, a filter chip, a select option. It
 *      returns the heading reading, and `.inline` / `.def` for inside a
 *      sentence. Nothing lowercases a word itself any more: a company's
 *      "Yard" stays "Yard", and `.inline` is what "yard" used to be computed
 *      with `.toLowerCase()` at ~45 call sites.
 *
 * Both read one vocabulary, resolved once on the server (`resolveVocabulary`)
 * and carried on the public branding payload, so a heading and the sentence
 * under it cannot name the same people two ways.
 * ======================================================================== */

export type TermName =
  | 'employee'
  | 'employees'
  | 'worker'
  | 'workers'
  | 'staffMember'
  | 'staff'
  | 'worksite'
  | 'worksites'
  | 'camp'
  | 'camps'
  | 'department'
  | 'departments'
  | 'team'
  | 'teams';

export type Terms = Record<TermName, string>;

/** The heading readings, plus the in-sentence ones. */
export type ResolvedTerms = Terms & { inline: Terms; def: Terms };

/** The office word set — what every screen reads before branding answers. */
export const DEFAULT_VOCABULARY: Vocabulary = WORD_SETS.hr;

/** Which of the vocabulary's languages a UI language reads. */
export function termLang(lng: string | undefined): TermLang {
  return lng?.startsWith('ar') ? 'ar' : 'en';
}

/** One language's words, in every reading. */
export function resolveTerms(vocab: Vocabulary, lang: TermLang): ResolvedTerms {
  const label = {} as Terms;
  const inline = {} as Terms;
  const def = {} as Terms;
  for (const key of TERM_KEYS) {
    for (const form of TERM_FORMS) {
      const name = termName(key, form) as TermName;
      const r = termReadings(vocab[lang][key] ?? WORD_SETS.hr[lang][key], form, lang);
      label[name] = r.label;
      inline[name] = r.inline;
      def[name] = r.def;
    }
  }
  return { ...label, inline, def };
}

/**
 * English "a" / "an" before a noun. By sound where spelling misleads — "a
 * user", "a one-off", "an hour" — which the first-vowel rule got wrong.
 */
export function withArticle(noun: string): string {
  const w = noun.trim().toLowerCase();
  const vowelSound = /^(hour|honest|honou?r|heir)/.test(w)
    ? true
    : /^(u[bcfhjkqrstn][aeiou]|use|eu|one|once|uni)/.test(w)
      ? false
      : /^[aeiou]/.test(w);
  return `${vowelSound ? 'an' : 'a'} ${noun}`;
}

let active: Vocabulary = DEFAULT_VOCABULARY;

function publish(): void {
  const interpolation = i18n.options.interpolation;
  if (!interpolation) return;
  const lang = termLang(i18n.language);
  interpolation.defaultVariables = { term: termVariables(active[lang], lang) };
}

// A language switch changes which half of the vocabulary the copy reads.
i18n.on('languageChanged', publish);

/**
 * Make `vocab` the words every translated string reads. Called whenever public
 * branding loads or refreshes; `termsChanged` is bound in i18n.ts so every
 * `useTranslation` consumer re-renders with the new words.
 */
export function applyVocabulary(vocab: Vocabulary | undefined | null): void {
  active = vocab ?? DEFAULT_VOCABULARY;
  publish();
  i18n.emit('termsChanged');
}
